import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import PopoverMenu, {
    PopoverMenuProps,
} from '@deltek/specification-client/core/Navigation/PopoverMenu'
import PopoverMenuItem from '@deltek/specification-client/core/Navigation/PopoverMenuItem'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import React from 'react'
import { HiQuestionMarkCircle } from 'shared/HarmonyIcon'
import {
    harmonyPopoverPaper,
    useHarmonyIconButtonStyles,
} from 'shared/hooks/useHarmonyIconButtonStyles'
import HarmonyPopoverTrigger from './HarmonyPopoverTrigger'

const useStyles = makeStyles((theme: Theme) => ({
    paper: harmonyPopoverPaper(theme),
    buildVersion: {
        display: 'inline-block',
        width: '100%',
        textAlign: 'center',
        fontSize: pxToRem(10),
        color: theme.palette.grey[500],
        padding: `${theme.spacing(0.5)}px 0`,
    },
}))

// PopoverMenu forwards unknown props straight to MUI's <Menu> (confirmed in its source), which
// does accept `classes`, but the vendor's own type declarations don't list it.
const PopoverMenuWithClasses = PopoverMenu as React.FC<
    PopoverMenuProps & { classes?: { paper?: string } }
>

export interface HarmonyHelpButtonProps {
    helpMenuItems: MenuItemConfig[]
    buildNumber?: string
}

/**
 * Harmony's Help trigger, replacing the vendor's HelpMenuDropdown icon. Reuses the same
 * helpMenuItems content and the vendor's generic PopoverMenu/PopoverMenuItem, so the menu
 * itself (labels, links, conditions) stays exactly as-is — only the trigger icon changes.
 */
const HarmonyHelpButton = ({
    helpMenuItems,
    buildNumber,
}: HarmonyHelpButtonProps) => {
    const classes = useHarmonyIconButtonStyles()
    const popoverClasses = useStyles()
    const theme = useTheme()

    return (
        <HarmonyPopoverTrigger
            testId="buttonHelp"
            ariaLabel="Help"
            icon={
                <HiQuestionMarkCircle
                    className={classes.icon}
                    strokeWidth={1.6}
                />
            }
        >
            {({ anchorEl, onClose }) => (
                <PopoverMenuWithClasses
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    classes={{ paper: popoverClasses.paper }}
                >
                    {helpMenuItems.map((item) => (
                        <PopoverMenuItem
                            key={item.dataTestId ?? item.label}
                            data-testid={item.dataTestId}
                            text={item.label}
                            color={theme.harmony.colors.textHeader}
                            onClick={
                                typeof item.onClick === 'string'
                                    ? () =>
                                          navigateToExternalLink?.(
                                              item.onClick as string
                                          )
                                    : item.onClick
                            }
                        />
                    ))}
                    {buildNumber && (
                        <span className={popoverClasses.buildVersion}>
                            {`v.${buildNumber}`}
                        </span>
                    )}
                </PopoverMenuWithClasses>
            )}
        </HarmonyPopoverTrigger>
    )
}

export default HarmonyHelpButton
